// Admin-managed home banners (GET /api/banners/active, backend/routes/banners.py).
//
// The endpoint returns a bare list sorted by `sort_order`. `title`, `subtitle`
// and `cta_text` are `{en, ar}` maps; the image is stored as `image_url`
// (`image` is accepted too). `link` is "" or one of the forms validated by the
// backend (G8): /product/<id>, /shop?cat=<slug>, /<category-slug>, /shop,
// /collections[/<slug>], /brands[/<slug>] (Discover) or an
// https://anum360.ae/... URL. Rows saved before G8 may still hold free text, so
// the parser is tolerant: anything it does not understand gets no link.

import { pickLocalized } from './localized';

export { pickLocalized };

const SITE_HOSTS = new Set(['anum360.ae', 'www.anum360.ae']);
const MAX_SLIDES = 8;

// Top-level web pages (App.js). A single-segment banner link that is not one
// of these is a category landing page (`/<category-slug>`).
const WEB_PAGES = new Set([
  'about', 'contact', 'faq', 'privacy', 'terms', 'shipping-policy', 'returns',
  'gift-cards', 'wholesale', 'blog', 'vip', 'personal-shopping', 'pre-order',
  'corporate-gifting', 'sale', 'account', 'orders', 'wishlist', 'cart',
  'brands', 'collections',
]);
const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,63}$/i;

const productPath = (id) => `/products/${encodeURIComponent(id)}`;
const categoryPath = (slug) => `/products?cat=${encodeURIComponent(slug.toLowerCase())}`;

const safeDecode = (value) => {
  try { return decodeURIComponent(value); } catch { return value; }
};

// Site path (+ query/hash) → web route.
const mapSitePath = (url) => {
  const segments = url.pathname.split('/').filter(Boolean).map(safeDecode);
  const params = [...url.searchParams.entries()];
  const head = (segments[0] || '').toLowerCase();

  if (segments.length === 2 && !params.length) {
    const tail = segments[1];
    if (head === 'product' || head === 'products') return productPath(tail);
    if (['shop', 'category', 'categories'].includes(head) && SLUG_RE.test(tail)) return categoryPath(tail);
  }
  if (segments.length === 1 && (head === 'shop' || head === 'products')) {
    if (!params.length) return '/products';
    if (params.length === 1 && ['cat', 'category'].includes(params[0][0]) && SLUG_RE.test(params[0][1])) {
      return categoryPath(params[0][1]);
    }
    return `/products${url.search}`;
  }
  if (segments.length === 1 && !params.length && !url.hash && SLUG_RE.test(head) && !WEB_PAGES.has(head)) {
    return categoryPath(head);
  }
  return `${url.pathname}${url.search}${url.hash}`;
};

/**
 * Map a banner `link` to an in-app route for the router `Link`, or null when
 * the banner has no (usable) link. anum360.ae URLs become in-app paths; any
 * other host, scheme or malformed value is dropped.
 */
export const bannerHref = (raw) => {
  const value = typeof raw === 'string' ? raw.trim() : '';
  if (!value || /[\s\\]/.test(value) || value.startsWith('//')) return null;

  let url;
  try {
    if (value.startsWith('/')) {
      url = new URL(value, 'https://anum360.ae');
    } else {
      url = new URL(value);
      if (!['https:', 'http:'].includes(url.protocol)) return null;
      if (!SITE_HOSTS.has(url.hostname.toLowerCase()) || url.username || url.password || url.port) return null;
    }
  } catch {
    return null;
  }
  return mapSitePath(url);
};

/** Tolerant parse of the /banners/active payload into display-ready slides. */
export const normalizeBanners = (data) => {
  if (!Array.isArray(data)) return [];
  return data
    .filter((row) => row && typeof row === 'object' && (typeof row.id === 'string' || typeof row.id === 'number'))
    .map((row, order) => {
      const image = [row.image_url, row.image].find((v) => typeof v === 'string' && /^https?:\/\//i.test(v.trim()));
      return {
        id: String(row.id),
        title: row.title,
        subtitle: row.subtitle,
        cta: row.cta_text,
        image: image ? image.trim() : '',
        href: bannerHref(row.link),
        sortOrder: Number.isFinite(row.sort_order) ? row.sort_order : 0,
        order,
      };
    })
    .filter((b) => b.image || pickLocalized(b.title, 'en'))
    .sort((a, b) => a.sortOrder - b.sortOrder || a.order - b.order)
    .slice(0, MAX_SLIDES);
};
